import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  IPreAuthQuery,
  IPreAuthQueryReply,
  QueryData,
} from "../interfaces/IPreAuthQuery";
import { listHmoQueries } from "../apis/hmoQueryService";
import { QueryService } from "../apis/PreAuthQueryService";
import {
  groupQueriesByQueryStage,
  submitHmoQueryReply,
} from "../utils/queryRespondUtils";
import { getQueryPanelStageKey } from "../components/patient/patient-details/newPatientDetailsConstants";

interface QueryState {
  data: QueryData[];
  status: "idle" | "loading" | "succeeded" | "failed";
  loading  : boolean;
  error: string | null;
  addQueryStatus: "idle" | "loading" | "succeeded" | "failed";
  replyQueryStatus: "idle" | "loading" | "succeeded" | "failed";
  replyData: IPreAuthQueryReply;
}

const initialState: QueryState = {
  data: [],
  loading:false,
  status: "idle",
  error: null,
  addQueryStatus: "idle",
  replyQueryStatus: "idle",
  replyData: {
    returnedResponse: "",
    returnedDocumentUrl: [],
  },
};

export const fetchQueries = createAsyncThunk(
  "queries/fetchQueries",
  async (
    {
      caseId,
      visitId,
      portalMetaDataId,
      stageId,
      visitStages,
    }: {
      caseId: string | number;
      visitId: string | number;
      portalMetaDataId?: string | number;
      stageId?: any;
      visitStages?: Array<Record<string, any>> | null;
    },
    { rejectWithValue }
  ) => {
    try {
      const queries = await listHmoQueries(visitId);
      const grouped = groupQueriesByQueryStage(queries, visitStages);

      if (stageId == null || stageId === "") {
        return grouped;
      }

      const panelKeys = (Array.isArray(stageId) ? stageId : [stageId])
        .map((sid) => {
          const panel = visitStages?.find(
            (stage) => String(stage?.visitStageId) === String(sid),
          );
          return panel ? getQueryPanelStageKey(panel) : String(sid);
        })
        .filter(Boolean);

      if (!panelKeys.length) {
        return grouped;
      }

      const filtered: Record<string, QueryData[]> = {};
      for (const key of panelKeys) {
        filtered[key] = [...(grouped[key] || [])];
      }
      return filtered;
    } catch (error) {
      return rejectWithValue("Failed to fetch queries");
    }
  }
);

export const addQuery = createAsyncThunk(
  "queries/addQuery",
  async (
    {
      queryData,
      caseId,
      visitId,
      portalMetaDataId,
    }: {
      queryData: IPreAuthQuery;
      caseId: string | number;
      visitId: string | number;
      portalMetaDataId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await QueryService.addQuery(
        queryData,
        caseId,
        visitId,
        portalMetaDataId
      );
      return response;
    } catch (error) {
      return rejectWithValue("Failed to add query");
    }
  }
);

export const replyResponseToQuery = createAsyncThunk(
  "queries/replyQuery",
  async (
    {
      replyData,
      caseId,
      visitId,
      portalMetaDataId,
      casePreAuthQueryId,
    }: {
      replyData: IPreAuthQueryReply;
      caseId: string | number;
      visitId: string | number;
      portalMetaDataId?: string | number;
      casePreAuthQueryId: string | number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await submitHmoQueryReply(
        visitId,
        casePreAuthQueryId,
        replyData.returnedResponse,
        (replyData.returnedDocumentUrl || []).map((doc: any) => ({
          file: doc.file,
          documentType:
            doc.documentType || doc.documentLabelType || "OTHER",
          documentId: doc.id > 0 ? doc.id : undefined,
        })),
        {
          newDocumentId: replyData.newDocumentId,
          oldDocumentId: replyData.oldDocumentId,
        },
      );
      return response;
    } catch (error) {
      return rejectWithValue("Failed to add query");
    }
  }
);

const querySlice = createSlice({
  name: "queries",
  initialState,
  reducers: {
    resetAddQueryStatus: (state) => {
      state.addQueryStatus = "idle";
    },
    resetQueries: (state) => {
      state.data = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQueries.pending, (state) => {
        state.loading = true;
        state.data = [];
      })
      .addCase(fetchQueries.fulfilled, (state, action) => {
        state.loading = false;
        const grouped = action.payload as Record<string, QueryData[]>;
        state.data = Object.values(grouped).flat();
      })
      .addCase(fetchQueries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
      })
      // Add Query Cases
      .addCase(addQuery.pending, (state) => {
        state.addQueryStatus = "loading";
      })
      .addCase(addQuery.fulfilled, (state, action) => {
        state.addQueryStatus = "idle";

        // Optionally add the new query to the existing data
        state.data.push(action.payload);
      })
      .addCase(addQuery.rejected, (state, action) => {
        state.addQueryStatus = "failed";
        state.error = action.payload as string;
      })
      .addCase(replyResponseToQuery.pending, (state) => {
        state.replyQueryStatus = "loading";
      })
      .addCase(replyResponseToQuery.fulfilled, (state, action) => {
        state.replyQueryStatus = "succeeded";
        // Optionally add the new query to the existing data
        // state.replyData(action.payload);
      })
      .addCase(replyResponseToQuery.rejected, (state, action) => {
        state.replyQueryStatus = "failed";
        state.error = action.payload as string;
      });
  },
});

export const {
  resetQueries
} = querySlice.actions;

export default querySlice.reducer;
